import { t } from "../i18n";

/**
 * Turns any caught error (axios, fetch or thrown Error) into one short sentence a merchant can act on.
 *
 * Server validation messages and messages our code wrote for people are kept. Connection failures, timeouts,
 * server faults (5xx) and anything that still looks technical - status codes, exception names, URLs, raw JSON -
 * become plain language, or `fallback` when given.
 */

// Translated when used, so they follow the current language.
const GENERIC = "error.generic";
const NO_INTERNET = "error.no_internet";
const UNREACHABLE = "error.unreachable";
const TIMEOUT = "error.timeout";
const SERVER = "error.server";
const SESSION = "error.session";

const NETWORK_RE = /Network ?Error|Failed to fetch|Load failed|ERR_NETWORK|ERR_CONNECTION|ECONNREFUSED|ENOTFOUND|net::/i;
const TIMEOUT_RE = /timeout|timed out|ECONNABORTED|ETIMEDOUT/i;
const TECHNICAL_RE =
  /Exception|Error:|TypeError|SyntaxError|ReferenceError|Unexpected token|is not a function|undefined|\bnull\b|status code|^HTTP \d{3}$|https?:\/\/|\bJSON\b|browser console|stack|java\.|org\.springframework|\bSQL\b|<html|^\s*[[{]/i;

function isTechnical(msg) {
  return msg.length > 240 || TECHNICAL_RE.test(msg);
}

function serverDetail(data) {
  if (!data || typeof data !== "object") return null;
  const first = Array.isArray(data.details) ? data.details[0] : null;
  const m = (first && typeof first === "object" && first.message) || data.message;
  return typeof m === "string" && m.trim() && m.trim() !== "Invalid request" ? m.trim() : null;
}

export function friendlyError(err, fallback) {
  const message = friendlyErrorKeyed(err, fallback);
  return message.startsWith("error.") ? t(message) : message;
}

function friendlyErrorKeyed(err, fallback) {
  const generic = fallback || GENERIC;
  if (!err) return generic;

  const status = err.response?.status;
  if (status) {
    if (status >= 500) return SERVER;
    const detail = serverDetail(err.response.data);
    if (detail && !isTechnical(detail)) return detail;
    if (status === 401) return SESSION;
    if (status === 408 || status === 504) return TIMEOUT;
  }

  const raw = typeof err === "string" ? err : String(err.message || "");
  if (err.code === "ECONNABORTED" || err.code === "ETIMEDOUT" || (TIMEOUT_RE.test(raw) && !status)) return TIMEOUT;
  if (err.code === "ERR_NETWORK" || NETWORK_RE.test(raw)) {
    return typeof navigator !== "undefined" && navigator.onLine === false ? NO_INTERNET : UNREACHABLE;
  }
  const msg = raw.trim();
  if (!msg || isTechnical(msg)) return generic;
  return msg;
}
